/**
 * Carrying out a use case.
 *
 * Each entry says what it wants; this decides what that means in the app.
 * Deliberately thin — none of these do work themselves, they hand off to the
 * pipeline session, the tool session or a tab, so a use case is a label on an
 * existing path rather than a second implementation of it.
 */

import { useAppStore } from "@/store/appStore";
import { goToTab, pickAndOpenCohort } from "@/pipeline/actions";
import { usePipelineSession } from "@/pipeline/session";
import { useToolSession } from "@/tools/session";
import { TOOLS } from "@/tools/catalog";
import type { UseCase } from "./catalog";

/** Why this use case cannot be started right now, or null. */
export function useCaseIssue(u: UseCase, hasCase: boolean, engineReady: boolean): string | null {
  if (u.unavailable) return u.unavailable;
  // Opening data and checking the engine must work with nothing loaded —
  // they are how you get out of having nothing loaded.
  const needsNothing = u.category === "data" || u.id === "insp.engine";
  if (!hasCase && !needsNothing) return "Open a study first.";
  const action = u.action;
  if (action.kind === "pipeline" || action.kind === "batch") {
    if (!engineReady) return "No processing engine is ready. Open Developer → Engine.";
  }
  if (action.kind === "tool") {
    const tool = TOOLS.find((t) => t.id === action.toolId);
    // Inspecting a header is desktop work; running a stage is not.
    const needsEngine = tool ? Boolean(tool.stage || tool.op) : false;
    if (needsEngine && !engineReady) {
      return "No processing engine is ready. Open Developer → Engine.";
    }
  }
  return null;
}

/** One subject, or a folder of them. Chosen on the Start screen. */
export type RunScope = "one" | "many";

/**
 * `scope: "many"` runs the same analysis over a folder of subjects.
 *
 * It belongs here rather than in a separate "batch" use case because it is the
 * same three analyses either way — one subject or two hundred is a property of
 * the data, not of the pipeline, and having it as a fourth card said otherwise.
 */
export function startUseCase(u: UseCase, opts: { scope?: RunScope } = {}): void {
  if (opts.scope === "many" && u.action.kind === "pipeline") {
    const { pipelineKind, dwi, pwi } = u.action;
    const session = usePipelineSession.getState();
    session.startNewAnalysis(pipelineKind, "full");
    if (dwi || pwi) {
      session.patchDraft({ pipelineKind, stages: { dwi: dwi ?? [], pwi: pwi ?? [] } });
    }
    session.setDataMode("dataset");
    goToTab("pipeline");
    void pickAndOpenCohort();
    return;
  }

  switch (u.action.kind) {
    case "pipeline": {
      const { pipelineKind, dwi, pwi } = u.action;
      const session = usePipelineSession.getState();
      session.startNewAnalysis(pipelineKind, "full");
      if (dwi || pwi) {
        session.patchDraft({
          pipelineKind,
          stages: {
            dwi: dwi ?? [],
            pwi: pwi ?? [],
          },
        });
      }
      goToTab("pipeline");
      break;
    }
    case "batch": {
      const session = usePipelineSession.getState();
      session.startNewAnalysis(u.action.pipelineKind, "full");
      session.setDataMode("dataset");
      goToTab("pipeline");
      // And ask for the folder. Landing on an empty many-subject form looks
      // identical to the one-subject form, which is how a batch entrance can
      // exist in the menu and still be impossible to find.
      void pickAndOpenCohort();
      break;
    }
    case "tool": {
      useToolSession.getState().selectTool(u.action.toolId);
      // The viewer is where a tool runs; its parameters appear beside it.
      goToTab("view");
      break;
    }
    case "screen": {
      goToTab(u.action.tab);
      break;
    }
  }
}

/** The engine-ready flag the Start screen gates on. */
export function useEngineReady(): boolean {
  return useAppStore((s) => s.systemStatus?.engine.ready === true);
}
